import { MaterialCommunityIcons } from "@expo/vector-icons";
import { addDays, addMonths, endOfMonth, isAfter, isBefore, isSameDay, isSameMonth, startOfMonth, startOfWeek, } from "date-fns";
import React, { useMemo, useRef, useState } from "react";
import { Platform, Pressable, StyleSheet, View } from "react-native";
import { useTranslation } from "@luna-restaurant/i18n";
import { useCustomTheme } from "../hooks/useCustomTheme";
import { TextInput } from "./TextInput";
import { ThemedDropdown } from "./ThemedDropdown";
import { ThemedText } from "./ThemedText";
export const DatePicker = ({ value, onChange, placeholder, minDate, maxDate, initialMonth, isDateDisabled, onBlur, disabled = false, error, }) => {
    const { colors, fonts } = useCustomTheme();
    const { i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [monthCursor, setMonthCursor] = useState(value ? new Date(value) : (initialMonth ?? new Date()));
    const inputRef = useRef(null);
    const openDropdown = () => {
        if (disabled)
            return;
        setOpen(true);
        setMonthCursor(value ? new Date(value) : (initialMonth ?? new Date()));
    };
    const getWeekDays = () => {
        const baseDate = new Date(Date.UTC(2017, 0, 2)); // just a Monday
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
            weekDays.push(baseDate.toLocaleDateString(i18n.language, { weekday: "short" }));
            baseDate.setDate(baseDate.getDate() + 1);
        }
        return weekDays;
    };
    const monthMatrix = useMemo(() => {
        const start = startOfWeek(startOfMonth(monthCursor), { weekStartsOn: 1 });
        const end = addDays(endOfMonth(monthCursor), 6);
        const weeks = [];
        let current = start;
        while (current <= end) {
            const week = [];
            for (let i = 0; i < 7; i++) {
                week.push(current);
                current = addDays(current, 1);
            }
            weeks.push(week);
        }
        return weeks;
    }, [monthCursor]);
    const isDisabledDate = (date) => {
        return ((minDate && isBefore(date, minDate)) ||
            (maxDate && isAfter(date, maxDate)) ||
            (isDateDisabled && isDateDisabled(date)));
    };
    const handleSelect = (date) => {
        if (isDisabledDate(date))
            return;
        if (onBlur)
            onBlur({});
        onChange(date.toLocaleDateString("en-CA"));
        setOpen(false);
    };
    const formatted = value
        ? new Date(value).toLocaleDateString(i18n.language, {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        })
        : "";
    return (<View style={styles.formGroup}>
      <Pressable ref={inputRef} onPress={openDropdown} onHoverIn={() => !disabled && setIsHovered(true)} onHoverOut={() => setIsHovered(false)} onFocus={() => !disabled && setIsFocused(true)} onBlur={(e) => {
            setIsFocused(false);
            if (onBlur)
                onBlur(e);
        }} disabled={disabled} focusable={!disabled} style={Platform.OS === "web"
            ? {
                outlineStyle: "none",
                ...(disabled ? { cursor: "not-allowed" } : {}),
            }
            : undefined} accessibilityRole="button" accessibilityState={{ disabled }} accessibilityLabel={placeholder}>
        <TextInput type="text" value={formatted} placeholder={placeholder} icon={{ iconSet: "material-community", icon: "calendar-range" }} readonly disabled={disabled} error={error} isFocusedOverride={open || isFocused} isHoveredOverride={isHovered}/>
      </Pressable>

      {open ? (<ThemedDropdown open={open} yOffset={-30} anchorRef={inputRef} onClose={() => setOpen(false)} testIDBackdrop="datepicker-backdrop">
          <View style={styles.calendarContainer}>
            <View style={styles.header}>
              <Pressable onPress={() => setMonthCursor((d) => addMonths(d, -1))} style={styles.navButton}>
                <MaterialCommunityIcons name="chevron-left" size={28} color={colors.text}/>
              </Pressable>
              <ThemedText type="subtitle">
                {monthCursor.toLocaleDateString(i18n.language, {
                month: "long",
                year: "numeric",
            })}
              </ThemedText>
              <Pressable onPress={() => setMonthCursor((d) => addMonths(d, 1))} style={styles.navButton}>
                <MaterialCommunityIcons name="chevron-right" size={28} color={colors.text}/>
              </Pressable>
            </View>

            <View style={styles.weekdays}>
              {getWeekDays().map((d) => (<ThemedText key={d} style={styles.weekday}>
                  {d}
                </ThemedText>))}
            </View>

            <View style={styles.weeks}>
              {monthMatrix.map((week, wi) => (<View key={`w-${wi}`} style={styles.weekRow}>
                  {week.map((date, di) => {
                    const disabledCell = isDisabledDate(date);
                    const selected = value && isSameDay(value, date);
                    const inMonth = isSameMonth(date, monthCursor);
                    const isToday = isSameDay(date, new Date());
                    return (<Pressable key={`d-${wi}-${di}`} onPress={() => handleSelect(date)} disabled={disabledCell} style={[
                            styles.dayCell,
                            !inMonth && styles.dayCellOutside,
                            selected && [
                                styles.dayCellSelected,
                                { borderColor: colors.border },
                            ],
                            disabledCell && styles.dayCellDisabled,
                        ]} testID={`datepicker-day-${date.toLocaleDateString("en-CA")}`}>
                        <ThemedText style={[styles.dayLabel, isToday && fonts.bold]}>
                          {date.toLocaleDateString(i18n.language, {
                            day: "numeric",
                        })}
                        </ThemedText>
                      </Pressable>);
                })}
                </View>))}
            </View>
          </View>
        </ThemedDropdown>) : null}
    </View>);
};
const styles = StyleSheet.create({
    formGroup: {
        marginBottom: 0,
        position: "relative",
    },
    calendarContainer: {
        gap: 12,
    },
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 4,
    },
    navButton: {
        padding: 8,
    },
    weekdays: {
        flexDirection: "row",
        justifyContent: "space-between",
    },
    weekday: {
        width: 48,
        textAlign: "center",
    },
    weeks: {
        gap: 8,
    },
    weekRow: {
        flexDirection: "row",
        justifyContent: "space-between",
        gap: 4,
    },
    dayCell: {
        width: 48,
        height: 40,
        justifyContent: "center",
        alignItems: "center",
    },
    dayCellSelected: {
        borderWidth: 3,
    },
    dayCellOutside: {
        opacity: 0.6,
    },
    dayCellDisabled: {
        opacity: 0.3,
    },
    dayLabel: {
        fontSize: 16,
    },
});
//# sourceMappingURL=DatePicker.js.map